import {Spec} from 'immutability-helper';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ClientId} from 'notebook/ELNConstants';
import {PLATE_MAP_PLUGIN_VIRTUALIZED_HEIGHT} from 'notebook/integrations/plate-maps/PlateMapPluginConstants';
import MultiScratchPlateNotePartView from 'notebook/integrations/plate-maps/scratch-plates/MultiScratchPlateNotePartView';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {MediocreItemAction} from 'notebook/mediocre/EditorTypes';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {
  GenericNoteLinkedScratchPlate,
  NotePart,
  ScratchPlateGroupData,
  ScratchPlateReferenceData,
} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';

type NoteLinkedScratchPlate = GenericNoteLinkedScratchPlate<ClientId>;
type ScratchPlateNotePart = NotePart & {item: NoteLinkedScratchPlate};

interface ScratchPlateItemProps {}

function normalizeScratchPlatesData(data: ScratchPlateGroupData): ScratchPlateGroupData {
  return {
    scratchPlates: data.scratchPlates.map(
      (scratchPlate): ScratchPlateReferenceData => ({
        scratchPlateId: scratchPlate.scratchPlateId,
        scratchPlateName: scratchPlate.scratchPlateName ?? '',
      })
    ),
  };
}

const NOTE_LINKED_SCRATCH_PLATE_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.NOTE_LINKED_SCRATCH_PLATE,
  title: 'Scratch Plate',
  icon: 'plate',
  linkable: false,
  alignable: false,
  canPasteFromOtherTenant: false,
  component: {
    ...MediocrePlugins.selectablePluginBase,
    required: [],
    getItemProps(this: NoteEditor, _notePart: ScratchPlateNotePart): ScratchPlateItemProps {
      return {};
    },
    make(
      this: NoteEditor,
      notePart: ScratchPlateNotePart,
      _index: number,
      _editorProps: NoteEditorProps,
      _itemProps: ScratchPlateItemProps,
      _searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      const scratchPlates = notePart.item.data.scratchPlates;
      return (
        <SelectableNotePart indentation={notePart.indentation}>
          <MultiScratchPlateNotePartView notePartCid={notePart.item.cid} scratchPlates={scratchPlates} />
        </SelectableNotePart>
      );
    },
    getVirtualizedHeight: (_item: ScratchPlateNotePart, _isCollapsed: boolean) =>
      PLATE_MAP_PLUGIN_VIRTUALIZED_HEIGHT,
  },
  itemToText(this: NoteEditor, item: ScratchPlateNotePart): string {
    const scratchPlates = item.item.data.scratchPlates;
    return scratchPlates
      .map((scratchPlate) => scratchPlate.scratchPlateName || scratchPlate.scratchPlateId)
      .join(', ');
  },
  itemToHTML(this: NoteEditor, $el: JQuery, _item: ScratchPlateNotePart): JQuery {
    return $el;
  },
  itemFromHTML(this: NoteEditor, _$el: JQuery, _item: ScratchPlateNotePart): null {
    notifications.warn('Scratch plates cannot be pasted.');
    return null;
  },
  getContent(this: NoteEditor, {item}: ScratchPlateNotePart): {item: NoteLinkedScratchPlate} {
    return {
      item: {
        ...item,
        data: normalizeScratchPlatesData(item.data),
      },
    };
  },
  setContent(this: NoteEditor, _data: unknown): Spec<NotePart> {
    throw new Error('Cannot set content for scratch plates.');
  },
  getReasonForDisallowedAction(
    this: NoteEditor,
    _item: ScratchPlateNotePart,
    _action: MediocreItemAction
  ): string | null {
    return null;
  },
};

export default NOTE_LINKED_SCRATCH_PLATE_PLUGIN;
